import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Sparkle, PaperPlaneRight,
  User as UserIcon, Robot,
  CheckCircle, DownloadSimple, UploadSimple,
  XCircle, Warning, Spinner, FileXls,
  ChatCircle, Table, /* Trash, */ Gear, Plus,
  ArrowLeft,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function fmt(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ─────────────────────────────────────────────────────────────────────────────
// Markdown renderer
// ─────────────────────────────────────────────────────────────────────────────

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p:      ({ node, ...p }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-bold text-gray-900" {...p} />,
          ul:     ({ node, ...p }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...p} />,
          ol:     ({ node, ...p }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...p} />,
          li:     ({ node, ...p }) => <li className="text-sm" {...p} />,
          h3:     ({ node, ...p }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...p} />,
          code:   ({ node, inline, ...p }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...p} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...p} />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline field widgets (identical to AssetTransferAgentModal)
// ─────────────────────────────────────────────────────────────────────────────

function AssetSearchWidget({ fieldUi, onPick, onSearch }) {
  const [q, setQ] = React.useState("");
  const allRows   = fieldUi.rows || [];

  const filtered = q
    ? allRows.filter((r) =>
        (r.cells || []).some((c) => String(c).toLowerCase().includes(q.toLowerCase()))
      )
    : allRows;

  const handleSearch = () => { if (q.trim()) onSearch?.(q.trim()); };
  const columns = fieldUi.columns || [];

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center border-b border-gray-100">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
          placeholder={fieldUi.placeholder || "Search by asset number or description…"}
          className="flex-1 px-3 py-2 text-xs outline-none bg-white"
        />
        <button
          onClick={handleSearch}
          className="px-3 py-2 text-xs text-[#0020C2] font-semibold border-l border-gray-100 hover:bg-[#0020C2]/5"
        >
          Search Oracle
        </button>
      </div>
      {columns.length > 0 && (
        <div
          className="grid text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50 px-3 py-1.5 border-b border-gray-100"
          style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
        >
          {columns.map((c) => <span key={c}>{c}</span>)}
        </div>
      )}
      <div className="max-h-56 overflow-y-auto">
        {allRows.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            No assets found. Type an asset number or description and click Search Oracle.
          </div>
        )}
        {allRows.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            No matches for "{q}" —{" "}
            <button onClick={handleSearch} className="text-[#0020C2] underline">search Oracle</button>
          </div>
        )}
        {filtered.slice(0, 100).map((row, i) => (
          <div
            key={i}
            onClick={() => onPick({ label: row.label, value: row.value,
              asset_number: row.asset_number, asset_description: row.asset_description })}
            className="grid px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
            style={{ gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, 1fr)` }}
          >
            {(row.cells || [row.label]).map((cell, ci) => (
              <span key={ci} className="text-gray-700 truncate pr-2">{cell}</span>
            ))}
          </div>
        ))}
      </div>
      {filtered.length > 100 && (
        <div className="px-3 py-1.5 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100">
          Showing 100 of {filtered.length} — type to filter or search Oracle
        </div>
      )}
    </div>
  );
}

function TableSelectOptions({ fieldUi, onPick }) {
  const rows    = fieldUi.rows    || [];
  const columns = fieldUi.columns || [];
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <div
        className="grid text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50 px-3 py-1.5 border-b border-gray-100"
        style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
      >
        {columns.map((c) => <span key={c}>{c}</span>)}
      </div>
      <div className="max-h-52 overflow-y-auto">
        {rows.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No items found.</div>
        )}
        {rows.map((row, i) => (
          <div
            key={i}
            onClick={() => onPick({ label: row.label, value: row.value, category_id: row.category_id })}
            className="grid px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
            style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
          >
            {(row.cells || []).map((cell, ci) => (
              <span key={ci} className="text-gray-700 truncate pr-2">{cell}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SearchableChatOptions({ fieldUi, onPick }) {
  const [q, setQ]  = React.useState("");
  const options    = fieldUi.options || [];
  const filtered   = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
         data-testid={`chat-field-ui-${fieldUi.field}`}>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${(fieldUi.label || fieldUi.field).replace(/_/g, " ")}…`}
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
      />
      <div className="max-h-44 overflow-y-auto">
        {options.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No options available. Check your Oracle connection.</div>
        )}
        {options.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No matches for "{q}"</div>
        )}
        {filtered.slice(0, 60).map((opt) => (
          <div
            key={opt.value}
            onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
          >
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function RadioOptions({ fieldUi, onPick }) {
  const options = fieldUi.options || [];
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`chat-field-ui-${fieldUi.field}`}>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onPick(opt)}
          className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors"
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat bubble
// ─────────────────────────────────────────────────────────────────────────────

function RetirementChatBubble({ msg, onOptionClick, onAssetSearch, isLastAssistant }) {
  const isUser  = msg.role === "user";
  const fieldUi = !isUser && !msg.loading && isLastAssistant ? msg.result?.next_field_ui : null;
  const uiType  = fieldUi?.uiType;

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white
          ${isUser ? "bg-[#0020C2]" : "bg-gray-700"}`}>
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>

      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed
          ${isUser
            ? "bg-[#0020C2] text-white rounded-tr-sm"
            : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"
          }`}>
        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}

        {!msg.loading && msg.text && (
          isUser
            ? <p className="whitespace-pre-wrap">{msg.text}</p>
            : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}

        {uiType === "searchable_dropdown" && (
          <SearchableChatOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {uiType === "radio" && (
          <RadioOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {uiType === "table_select" && (
          <TableSelectOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {uiType === "asset_search" && (
          <AssetSearchWidget
            fieldUi={fieldUi}
            onPick={(opt) => onOptionClick?.(opt)}
            onSearch={(query) => onAssetSearch?.(query)}
          />
        )}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Progress bar
// ─────────────────────────────────────────────────────────────────────────────

function ProgressBar({ progress }) {
  if (!progress) return null;
  const pctMatch  = progress.match(/\((\d+)%\)/);
  const stepMatch = progress.match(/(\d+)\/(\d+)/);
  if (!pctMatch || !stepMatch) return null;
  const pct = parseInt(pctMatch[1], 10);
  const [, done, total] = stepMatch;
  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Step {done} of {total}</span>
        <span>{pct}% complete</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#0020C2] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Input bar
// ─────────────────────────────────────────────────────────────────────────────

function RetirementInputBar({ inputRef, value, onChange, onKeyDown, onSend, busy }) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50 focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder='Describe the asset or type a number — e.g. "Retire the diesel generator"'
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }}
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0"
        >
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Success banner
// ─────────────────────────────────────────────────────────────────────────────

function SuccessBanner({ reply, onClose }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 bg-gray-50">
      <div className="w-16 h-16 rounded-full bg-[#0020C2]/10 flex items-center justify-center mb-5">
        <CheckCircle size={36} weight="fill" className="text-[#0020C2]" />
      </div>
      <h2 className="text-xl font-black text-gray-900 mb-2">Asset Retired!</h2>
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <ChatMarkdown>{reply}</ChatMarkdown>
      </div>
      <button
        onClick={onClose}
        className="mt-6 px-6 py-2 rounded-lg text-sm font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] transition-colors"
      >
        Close
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wizard panel — retirement field summary
// ─────────────────────────────────────────────────────────────────────────────

function RetirementWizardPanel({ fieldValues }) {
  const retType = fieldValues.retirement_type;

  const baseFields = [
    { key: "book_type_code",   label: "Asset Book" },
    { key: "asset_type",       label: "Asset Type" },
    { key: "asset_category",   label: "Category" },
    { key: "asset_number",     label: "Asset Number" },
    { key: "description",      label: "Description" },
    { key: "current_location", label: "Location" },
  ];

  const typeFields =
    retType === "PARTIAL_COST"
      ? [
          { key: "current_cost",  label: "Available Cost" },
          { key: "cost_retired",  label: "Cost to Retire" },
        ]
      : retType === "UNITS"
      ? [
          { key: "current_units",          label: "Available Units" },
          { key: "units_retired",          label: "Units to Retire" },
          { key: "date_retired",           label: "Retirement Date" },
          { key: "retirement_conv_type_id",label: "Convention ID" },
        ]
      : retType === "FULL_COST"
      ? [
          { key: "current_cost", label: "Full Cost (auto)" },
        ]
      : [];

  const typeLabel = {
    FULL_COST:    "💰 Full Cost Retirement",
    PARTIAL_COST: "📊 Partial Cost Retirement",
    UNITS:        "📦 Retirement by Units",
  }[retType] || null;

  const fields = [...baseFields, ...typeFields];

  return (
    <div className="space-y-3">
      {typeLabel && (
        <div className="text-[10px] font-bold px-2 py-1 rounded bg-[#0020C2]/10 text-[#0020C2] inline-block">
          {typeLabel}
        </div>
      )}
      {fields.map(({ key, label }) => {
        const val = fieldValues[key];
        return (
          <div key={key} className="space-y-0.5">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
            <div className={`text-sm px-3 py-2 rounded border ${
              val
                ? "border-[#0020C2]/20 bg-[#0020C2]/5 text-gray-900 font-medium"
                : "border-gray-100 bg-gray-50 text-gray-400 italic"
            }`}>
              {val || "Not yet provided"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk upload row table
// ─────────────────────────────────────────────────────────────────────────────

// ── Bulk upload constants (match AssetAgentModal pattern) ─────────────────────
// REQUIRED_COLUMNS must match the Python bulk module REQUIRED_COLUMNS exactly
const BULK_REQUIRED_COLUMNS = [
  "book_type_code",
  "asset_number",
  "retirement_type",
  "retirement_cost",
  "units_retired",
  "retirement_date",
];

const BULK_PREVIEW_COLS = [
  { key: "book_type_code",  label: "Asset Book" },
  { key: "asset_number",    label: "Asset Number" },
  { key: "retirement_type", label: "Type" },
  { key: "retirement_cost", label: "Cost" },
  { key: "units_retired",   label: "Units" },
  { key: "retirement_date", label: "Date" },
];

function BulkStatusBadge({ valid }) {
  return valid ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
      <CheckCircle size={10} weight="fill" /> Valid
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
      <XCircle size={10} weight="fill" /> Errors
    </span>
  );
}

// BulkUploadZone — matches AssetAgentModal's BulkUploadZone exactly
function BulkUploadZone({ onFileSelected, onDownloadTemplate, downloading }) {
  const inputRef = React.useRef(null);
  const [drag, setDrag] = React.useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) { alert("Only .xlsx or .xls files are accepted."); return; }
    onFileSelected(file);
  };

  return (
    <div className="flex flex-col items-center gap-6 py-10 px-8">
      <div className="w-full max-w-md bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <FileXls size={28} weight="duotone" className="text-[#0020C2] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Step 1 — Download Template</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Pre-filled with Oracle dropdowns for Asset Book and Retirement Type.
          </p>
        </div>
        <button
          onClick={onDownloadTemplate}
          disabled={downloading}
          className="shrink-0 flex items-center gap-1.5 text-xs font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] disabled:opacity-50 px-3 py-2 rounded-lg transition-colors"
        >
          {downloading ? <Spinner size={13} className="animate-spin" /> : <DownloadSimple size={13} weight="bold" />}
          Download
        </button>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`w-full max-w-md border-2 border-dashed rounded-xl p-10 flex flex-col items-center gap-3 cursor-pointer transition-colors
          ${drag ? "border-[#0020C2] bg-blue-50" : "border-gray-300 hover:border-[#0020C2] hover:bg-gray-50"}`}
      >
        <UploadSimple size={36} weight="duotone" className="text-gray-400" />
        <p className="text-sm font-semibold text-gray-700">Step 2 — Drop your filled Excel here</p>
        <p className="text-xs text-gray-400">or click to browse (.xlsx, .xls)</p>
        <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>

      <p className="text-xs text-gray-400 text-center max-w-md">
        All rows are validated against Oracle before anything is submitted.
      </p>
    </div>
  );
}

function BulkPreviewTable({ rows }) {
  return (
    <div className="overflow-auto border border-gray-200 rounded-xl">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 w-12">Row</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 w-20">Status</th>
            {BULK_PREVIEW_COLS.map((c) => (
              <th key={c.key} className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 whitespace-nowrap">{c.label}</th>
            ))}
            <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Errors</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.row_num} className={row.valid ? "hover:bg-gray-50" : "bg-red-50 hover:bg-red-100"}>
              <td className="px-3 py-2 border-b border-gray-100 font-mono text-gray-500">{row.row_num}</td>
              <td className="px-3 py-2 border-b border-gray-100"><BulkStatusBadge valid={row.valid} /></td>
              {BULK_PREVIEW_COLS.map((c) => (
                <td key={c.key} className="px-3 py-2 border-b border-gray-100 text-gray-700 max-w-[140px] truncate" title={row.data?.[c.key] || ""}>
                  {row.data?.[c.key] || <span className="text-gray-300">—</span>}
                </td>
              ))}
              <td className="px-3 py-2 border-b border-gray-100">
                {row.errors?.length > 0 ? (
                  <ul className="space-y-0.5">
                    {row.errors.map((e, i) => (
                      <li key={i} className="text-red-600 flex items-start gap-1">
                        <XCircle size={10} weight="fill" className="shrink-0 mt-0.5" /> {e}
                      </li>
                    ))}
                  </ul>
                ) : <span className="text-green-600">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main modal
// ─────────────────────────────────────────────────────────────────────────────

const SESSION_KEY = "asset_retirement_session_id";

export default function AssetRetirementAgentModal({ onClose }) {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();

  // Follow SupplierAgentModal: name/hint come from ErpContext agent catalog, not hardcoded.
  // const agent = agents?.find((a) => a.key === openAgent) || {
  //   label: "Asset Retirement",
  //   hint:  "Retire a fixed asset — Full Cost, Partial Cost, or by Units",
  // };
  const agent = agents?.find((a) => a.key === openAgent);

  const [tab,         setTab]         = React.useState("chat");
  const [sessionId,   setSessionId]   = React.useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [messages,    setMessages]    = React.useState([]);
  const [input,       setInput]       = React.useState("");
  const [busy,        setBusy]        = React.useState(false);
  const [progress,    setProgress]    = React.useState(null);
  const [retireDone,  setRetireDone]  = React.useState(false);
  const [successReply,setSuccessReply]= React.useState("");
  const [started,     setStarted]     = React.useState(false);
  const [fieldValues, setFieldValues] = React.useState({});
  const [chatStarted, setChatStarted] = React.useState(false);

  // Bulk upload state — stage-based like AssetAgentModal
  const BULK_STAGE = { UPLOAD: "UPLOAD", VALIDATING: "VALIDATING", PREVIEW: "PREVIEW", SUMMARY: "SUMMARY" };
  const [bulkStage,   setBulkStage]   = React.useState(BULK_STAGE.UPLOAD);
  const [bulkFile,    setBulkFile]    = React.useState(null);
  const [bulkParsed,  setBulkParsed]  = React.useState(null);
  const [bulkResults, setBulkResults] = React.useState(null);
  const [bulkLoading, setBulkLoading] = React.useState(false);
  const [bulkError,   setBulkError]   = React.useState("");

  const messagesEndRef = React.useRef(null);
  const inputRef       = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Initialize session ──────────────────────────────────────────────────────
  React.useEffect(() => {
    if (started) return;
    setStarted(true);

    const doInit = async () => {
      setBusy(true);
      const existingSid = sessionStorage.getItem(SESSION_KEY);

      if (existingSid) {
        setSessionId(existingSid);
        setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 150);
        return;
      }

      try {
        const { data } = await api.post("/erp-agents/asset-retirement/start");
        const sid = data.session_id;
        sessionStorage.setItem(SESSION_KEY, sid);
        setSessionId(sid);
      } catch (e) {
        console.warn("[AssetRetirement] /start failed:", e?.response?.data?.detail || e.message);
      } finally {
        setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 150);
      }
    };

    doInit();
  }, [started]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync fields_collected → wizard panel ────────────────────────────────────
  const syncFieldsToWizard = React.useCallback((fieldsCollected) => {
    if (!fieldsCollected || typeof fieldsCollected !== "object") return;
    setFieldValues((prev) => {
      const merged = { ...prev };
      if (fieldsCollected.retirement_type)         merged["retirement_type"]         = String(fieldsCollected.retirement_type);
      if (fieldsCollected.book_type_code)          merged["book_type_code"]           = String(fieldsCollected.book_type_code);
      if (fieldsCollected.asset_type)              merged["asset_type"]               = String(fieldsCollected.asset_type);
      if (fieldsCollected.category_name)           merged["asset_category"]           = String(fieldsCollected.category_name);
      else if (fieldsCollected.category_id)        merged["asset_category"]           = String(fieldsCollected.category_id);
      if (fieldsCollected.asset_number)            merged["asset_number"]             = String(fieldsCollected.asset_number);
      if (fieldsCollected.asset_description)       merged["description"]              = String(fieldsCollected.asset_description);
      if (fieldsCollected.current_location)        merged["current_location"]         = String(fieldsCollected.current_location);
      if (fieldsCollected.current_cost != null)    merged["current_cost"]             = fmt(fieldsCollected.current_cost);
      if (fieldsCollected.current_units != null)   merged["current_units"]            = String(fieldsCollected.current_units);
      if (fieldsCollected.cost_retired != null)    merged["cost_retired"]             = fmt(fieldsCollected.cost_retired);
      if (fieldsCollected.units_retired != null)   merged["units_retired"]            = String(fieldsCollected.units_retired);
      if (fieldsCollected.date_retired)            merged["date_retired"]             = String(fieldsCollected.date_retired);
      if (fieldsCollected.retirement_conv_type_id) merged["retirement_conv_type_id"]  = String(fieldsCollected.retirement_conv_type_id);
      return merged;
    });
  }, []);

  // ── Send a user message ─────────────────────────────────────────────────────
  const sendMessage = async (text) => {
    const userText = (text || "").trim();
    if (!userText || busy) return;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    const now = ts();
    setMessages((prev) => [
      ...prev,
      { id: Date.now(),     role: "user",      text: userText, timestamp: now },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);

    try {
      const { data } = await api.post("/erp-agents/asset-retirement/chat", {
        session_id: sessionId,
        message:    userText,
      });

      if (data.progress) setProgress(data.progress);
      if (data.fields_collected) syncFieldsToWizard(data.fields_collected);

      if (data.retirement_complete) {
        setRetireDone(true);
        setSuccessReply(data.reply || "Asset retired successfully!");
      }

      const reply = data.reply || data.response || data.message || "Done.";
      const now2  = ts();
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, text: reply, result: data, timestamp: now2 }
            : m
        )
      );
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e.message || "An error occurred.";
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, timestamp: ts(), text: `❌ ${errMsg}` }
            : m
        )
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  // ── New chat ────────────────────────────────────────────────────────────────
  const startNewChat = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    setInput("");
    setProgress(null);
    setRetireDone(false);
    setSuccessReply("");
    setStarted(false);
    setChatStarted(false);
    setFieldValues({});
  };

  const close = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setOpenAgent(null);
    onClose?.();
  };

  // ── Bulk upload handlers — match AssetAgentModal pattern exactly ─────────────
  const handleDownloadTemplate = async () => {
    setBulkLoading(true); setBulkError("");
    try {
      const resp = await api.get("/erp-agents/asset-retirement/bulk/template", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "asset_retirement_bulk_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setBulkError("Template download failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBulkLoading(false); }
  };

  const handleFileSelected = async (file) => {
    setBulkFile(file); setBulkError(""); setBulkStage(BULK_STAGE.VALIDATING);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(
        "/erp-agents/asset-retirement/bulk/validate-file",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setBulkParsed(data); setBulkStage(BULK_STAGE.PREVIEW);
    } catch (e) {
      setBulkError(e?.response?.data?.detail || e.message || "Validation failed.");
      setBulkStage(BULK_STAGE.UPLOAD);
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkParsed) return;
    const validRows = bulkParsed.rows.filter((r) => r.valid);
    if (!validRows.length) return;
    setBulkLoading(true); setBulkError("");
    try {
      const { data } = await api.post("/erp-agents/asset-retirement/bulk/submit", { rows: validRows });
      setBulkResults(data); setBulkStage(BULK_STAGE.SUMMARY);
    } catch (e) {
      setBulkError("Submission failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBulkLoading(false); }
  };

  const handleBulkReupload = () => {
    setBulkParsed(null); setBulkFile(null); setBulkError("");
    setBulkStage(BULK_STAGE.UPLOAD);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes retireChatFadeIn  { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes retireWizardSlide { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        @keyframes retireFadeIn      { from { opacity:0; transform:translateY(8px);   } to { opacity:1; transform:translateY(0); } }
        .retire-modal-pre  { width: 640px; max-width: calc(100vw - 2rem); max-height: 90vh; animation: retireFadeIn 0.3s ease; }
        .retire-modal-chat { width: min(92vw, 1400px); height: 90vh; animation: retireFadeIn 0.3s ease; }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={close}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`agent-modal-${openAgent}`}
          className={`bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden shadow-2xl
            ${chatStarted ? "retire-modal-chat" : "retire-modal-pre"}`}
          style={{ transition: "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {/* ── Header ── */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP · Vaku AI
              </div>
              {/* <h3 className="text-2xl font-black tracking-tight mt-0.5 flex items-center gap-2">
                <Trash size={22} className="text-[#0020C2]" weight="bold" />
                {agent.label}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent.hint}</p> */}
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{agent?.label}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent?.hint}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {/* Tabs */}
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setTab("chat")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    tab === "chat" ? "bg-white text-[#0020C2] shadow-sm font-semibold" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <ChatCircle size={12} /> Chat
                </button>
                <button
                  onClick={() => setTab("bulk")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    tab === "bulk" ? "bg-white text-[#0020C2] shadow-sm font-semibold" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Table size={12} /> Bulk Upload
                </button>
              </div>
              {chatStarted && tab === "chat" && (
                <>
                  <button
                    onClick={startNewChat}
                    className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0"
                    title="Start a new conversation"
                  >
                    <Plus size={13} weight="bold" /> New Chat
                  </button>
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button
                onClick={() => { close(); setSetupOpen?.(true); }}
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
              >
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="agent-modal-close" className="text-gray-400 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* ── CHAT TAB ── */}
          {tab === "chat" && (
            <>
              {retireDone ? (
                <SuccessBanner reply={successReply} onClose={close} />

              ) : !chatStarted ? (

                /* Pre-chat: wizard summary + input */
                <>
                  <div className="flex-1 overflow-y-auto p-5">
                    <p className="text-sm text-gray-600 mb-4">
                      Retire a fixed asset using <strong>Full Cost</strong>, <strong>Partial Cost</strong>, or{" "}
                      <strong>by Units</strong>. Select the retirement type when prompted, then follow the guided workflow.
                    </p>
                    <RetirementWizardPanel fieldValues={fieldValues} />
                  </div>
                  <RetirementInputBar
                    inputRef={inputRef}
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onSend={() => sendMessage(input)}
                    busy={busy}
                  />
                </>

              ) : (

                /* Chat mode: 60% chat / 40% wizard */
                <div className="flex flex-1 overflow-hidden">

                  {/* LEFT — chat panel */}
                  <div
                    className="flex flex-col overflow-hidden border-r border-gray-200"
                    style={{ width: "60%", animation: "retireChatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}
                  >
                    <ProgressBar progress={progress} />

                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                      {messages.map((msg) => {
                        const lastAssistantId = [...messages]
                          .reverse()
                          .find((m) => m.role === "assistant" && !m.loading)?.id;
                        const isLast = msg.role === "assistant" && msg.id === lastAssistantId;
                        return (
                          <RetirementChatBubble
                            key={msg.id}
                            msg={msg}
                            isLastAssistant={isLast}
                            onOptionClick={(opt) => {
                              const fui = [...messages].reverse()
                                .find((m) => m.role === "assistant" && !m.loading)
                                ?.result?.next_field_ui;
                              const field = fui?.field;

                              // Immediately update wizard for retirement_type selection
                              if (field === "retirement_type") {
                                setFieldValues((prev) => ({ ...prev, retirement_type: opt.value }));
                              }
                              sendMessage(opt.value);
                            }}
                            onAssetSearch={(query) => sendMessage(query)}
                          />
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>

                    <RetirementInputBar
                      inputRef={inputRef}
                      value={input}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onSend={() => sendMessage(input)}
                      busy={busy}
                    />
                  </div>

                  {/* RIGHT — wizard / summary panel */}
                  <div
                    className="flex flex-col bg-white"
                    style={{ width: "40%", minWidth: "360px", animation: "retireWizardSlide 0.45s cubic-bezier(0.4,0,0.2,1)" }}
                  >
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                        <Sparkle size={11} weight="fill" /> Retirement Details
                        <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
                          · auto-filled as you chat
                        </span>
                      </div>
                      <RetirementWizardPanel fieldValues={fieldValues} />
                    </div>
                  </div>

                </div>
              )}
            </>
          )}

          {/* ── BULK TAB — mirrors AssetAgentModal stage-based pattern exactly ── */}
          {tab === "bulk" && (
            <div className={`flex flex-col overflow-hidden flex-1 ${bulkStage !== BULK_STAGE.UPLOAD ? "h-full" : ""}`}>

              {/* UPLOAD stage */}
              {bulkStage === BULK_STAGE.UPLOAD && (
                <>
                  {bulkError && (
                    <div className="mx-5 mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 shrink-0">
                      <XCircle size={14} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">{bulkError}</p>
                    </div>
                  )}
                  <BulkUploadZone
                    onFileSelected={handleFileSelected}
                    onDownloadTemplate={handleDownloadTemplate}
                    downloading={bulkLoading}
                  />
                </>
              )}

              {/* VALIDATING stage */}
              {bulkStage === BULK_STAGE.VALIDATING && (
                <div className="flex flex-col items-center justify-center gap-4 py-16">
                  <Spinner size={36} className="animate-spin text-[#0020C2]" />
                  <p className="text-sm font-semibold text-gray-700">Validating {bulkFile?.name}…</p>
                  <p className="text-xs text-gray-400">Checking all rows against Oracle LOV data</p>
                </div>
              )}

              {/* PREVIEW stage */}
              {bulkStage === BULK_STAGE.PREVIEW && bulkParsed && (
                <div className="flex flex-col gap-4 p-5 h-full overflow-hidden">
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="flex-1 text-sm">
                      <span className="font-bold text-gray-900">{bulkFile?.name}</span>
                      <span className="text-gray-500 ml-2">— {bulkParsed.total_rows} row{bulkParsed.total_rows !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-semibold">
                      <span className="text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">✓ {bulkParsed.valid_rows} valid</span>
                      {bulkParsed.invalid_rows > 0 && (
                        <span className="text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">✗ {bulkParsed.invalid_rows} invalid</span>
                      )}
                    </div>
                  </div>

                  {bulkError && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 shrink-0">
                      <XCircle size={14} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">{bulkError}</p>
                    </div>
                  )}

                  {bulkParsed.invalid_rows > 0 && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 shrink-0">
                      <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">
                        {bulkParsed.invalid_rows} row{bulkParsed.invalid_rows !== 1 ? "s" : ""} will be skipped.
                        {bulkParsed.valid_rows > 0
                          ? ` Only the ${bulkParsed.valid_rows} valid row${bulkParsed.valid_rows !== 1 ? "s" : ""} will be submitted.`
                          : " Fix the errors and re-upload."}
                      </p>
                    </div>
                  )}

                  <div className="flex-1 overflow-auto min-h-0">
                    <BulkPreviewTable rows={bulkParsed.rows} />
                  </div>

                  <div className="flex items-center justify-between shrink-0 pt-2 border-t border-gray-200">
                    <button
                      onClick={handleBulkReupload}
                      disabled={bulkLoading}
                      className="text-xs font-semibold text-gray-500 hover:text-gray-800 flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <UploadSimple size={13} /> Upload different file
                    </button>
                    <button
                      onClick={handleBulkSubmit}
                      disabled={!bulkParsed.valid_rows || bulkLoading}
                      className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
                    >
                      {bulkLoading
                        ? <><Spinner size={14} className="animate-spin" /> Submitting…</>
                        : `Retire ${bulkParsed.valid_rows} Asset${bulkParsed.valid_rows !== 1 ? "s" : ""} in Oracle`}
                    </button>
                  </div>
                </div>
              )}

              {/* SUMMARY stage */}
              {bulkStage === BULK_STAGE.SUMMARY && bulkResults && (
                <div className="flex flex-col gap-5 p-6 h-full overflow-hidden">
                  <div className="flex flex-col items-center gap-3 py-4 shrink-0">
                    {bulkResults.failed === 0
                      ? <CheckCircle size={48} weight="fill" className="text-[#0020C2]" />
                      : <Warning size={48} weight="fill" className="text-amber-500" />}
                    <h2 className="text-xl font-black text-gray-900">
                      {bulkResults.failed === 0 ? "Bulk Retirement Complete!" : "Completed with Errors"}
                    </h2>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="font-semibold text-green-700">{bulkResults.succeeded} succeeded</span>
                      {bulkResults.failed > 0 && (
                        <span className="font-semibold text-red-700">{bulkResults.failed} failed</span>
                      )}
                      <span className="text-gray-400">(of {bulkResults.submitted} submitted)</span>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto min-h-0 border border-gray-200 rounded-xl">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 w-14">Row</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Result</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Asset</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Retirement ID</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Details / Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkResults.results.map((r) => (
                          <tr key={r.row_num} className={r.success ? "hover:bg-gray-50" : "bg-red-50 hover:bg-red-100"}>
                            <td className="px-3 py-2 border-b border-gray-100 font-mono text-gray-500">{r.row_num}</td>
                            <td className="px-3 py-2 border-b border-gray-100">
                              {r.success
                                ? <span className="flex items-center gap-1 text-green-700 font-semibold"><CheckCircle size={12} weight="fill" /> Retired</span>
                                : <span className="flex items-center gap-1 text-red-700 font-semibold"><XCircle size={12} weight="fill" /> Failed</span>}
                            </td>
                            <td className="px-3 py-2 border-b border-gray-100 font-mono text-[10px] text-gray-600">{r.asset_id || "—"}</td>
                            <td className="px-3 py-2 border-b border-gray-100 font-mono text-[10px] text-gray-600">{r.retirement_id || "—"}</td>
                            <td className="px-3 py-2 border-b border-gray-100 text-gray-600">
                              {r.success ? "Retired successfully" : (r.error || "Oracle failure")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between shrink-0 pt-2 border-t border-gray-200">
                    <button
                      onClick={handleBulkReupload}
                      className="text-xs font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1.5"
                    >
                      <UploadSimple size={13} /> Retire more assets
                    </button>
                    <button onClick={close} className="px-5 py-2 text-sm font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] rounded-lg transition-colors">
                      Done
                    </button>
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      </div>
    </>
  );
}